/**
 * As peças que as três telas do painel compartilham: o filtro de período e o KPI.
 *
 * Elas estão juntas porque a regra é a mesma nas três — **um filtro só, acima de tudo que ele
 * recorta**. Filtro dentro de cartão de gráfico faz dois gráficos da mesma tela responderem a
 * períodos diferentes, e ninguém percebe até comparar dois números que não podiam discordar.
 */
import { StatTile, Tooltip, cn } from '@mgdlead/ui';
import { useState } from 'react';
import { CalendarDays, CircleHelp } from 'lucide-react';

import {
  METRICA,
  PERIODOS,
  rotuloDoPeriodo,
  type Intervalo,
  type MetricaId,
  type PeriodoId,
} from './analytics-ui';

export interface PeriodFilterProps {
  value: PeriodoId;
  onChange: (value: PeriodoId) => void;
  /**
   * "📅 Datas": intervalo sob medida (pedido do dono, 06/10). Com intervalo ativo, nenhum
   * preset fica marcado — o período é o intervalo. Ausente = só os presets.
   */
  intervalo?: { value: Intervalo | null; onChange: (value: Intervalo | null) => void };
  /**
   * O fuso em que o servidor resolveu o recorte. Não vai mais escrito ao lado (o dono achou
   * ruído, 06/10): o relógio do topo mostra a hora no fuso do workspace.
   */
  timezone?: string;
  /**
   * `inline`: só o controle segmentado, para morar nas ações do `PageHeader`. Padrão: linha
   * própria.
   */
  inline?: boolean;
}

/**
 * O seletor de período.
 *
 * `<button>` num grupo com `role="radiogroup"`, e não um `<select>`: são quatro opções, todas
 * cabem, e um toque vale mais que dois no celular.
 */
export function PeriodFilter({ value, onChange, inline = false, intervalo }: PeriodFilterProps) {
  const [abrirDatas, setAbrirDatas] = useState(false);
  const custom = intervalo?.value ?? null;
  const controle = (
    <div className="inline-flex flex-wrap items-center gap-2">
      <div
        role="radiogroup"
        aria-label="Período"
        className="inline-flex items-center gap-0.5 rounded-pill bg-muted p-1"
      >
        {PERIODOS.map((p) => {
          const ativo = custom === null && p.id === value;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={ativo}
              onClick={() => {
                intervalo?.onChange(null);
                setAbrirDatas(false);
                onChange(p.id);
              }}
              className={cn(
                'hit-44 h-7 rounded-pill px-3.5 text-xs font-medium transition-colors duration-[var(--duration-micro)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                ativo
                  ? 'bg-surface text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {p.label}
            </button>
          );
        })}
        {intervalo ? (
          <button
            type="button"
            role="radio"
            aria-checked={custom !== null}
            aria-expanded={abrirDatas}
            onClick={() => setAbrirDatas((v) => !v)}
            className={cn(
              'hit-44 inline-flex h-7 items-center gap-1 rounded-pill px-3 text-xs font-medium transition-colors duration-[var(--duration-micro)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              custom !== null
                ? 'bg-surface text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CalendarDays aria-hidden="true" className="size-3.5" />
            {custom ? rotuloDoPeriodo(custom) : 'Datas'}
          </button>
        ) : null}
      </div>
      {intervalo && abrirDatas ? (
        <EscolherDatas
          inicial={custom}
          onAplicar={(v) => {
            intervalo.onChange(v);
            setAbrirDatas(false);
          }}
        />
      ) : null}
    </div>
  );

  if (inline) return controle;

  return <div className="mb-4 flex flex-wrap items-center justify-between gap-2">{controle}</div>;
}

/** Dois campos de data nativos (o celular abre o calendário dele) + Aplicar. */
function EscolherDatas({
  inicial,
  onAplicar,
}: {
  inicial: Intervalo | null;
  onAplicar: (v: Intervalo) => void;
}) {
  const [de, setDe] = useState(inicial?.from ?? '');
  const [ate, setAte] = useState(inicial?.to ?? '');
  const valido = de !== '' && ate !== '' && de <= ate;
  const campo =
    'h-9 rounded-md border border-border bg-surface px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div className="inline-flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface p-2 shadow-xs">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        De
        <input
          type="date"
          className={campo}
          value={de}
          max={ate || undefined}
          onChange={(e) => setDe(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Até
        <input
          type="date"
          className={campo}
          value={ate}
          min={de || undefined}
          onChange={(e) => setAte(e.target.value)}
        />
      </label>
      <button
        type="button"
        disabled={!valido}
        onClick={() => onAplicar({ from: de, to: ate })}
        className="h-9 rounded-pill bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        Aplicar
      </button>
    </div>
  );
}

export interface MetricTileProps {
  id: MetricaId;
  /** `null` = não há o que medir. Vira travessão **com a frase** de `METRICA[id].semDados`. */
  value: number | null;
  /**
   * Fração da variação contra o período anterior. `null` = sem base de comparação (pílula "—"
   * com o porquê no tooltip, G2); ausente = esta métrica não se compara.
   */
  delta?: number | null;
  comparedTo?: string;
  size?: 'compact' | 'default';
  loading?: boolean;
  /** Linha extra no rodapé do tile — é onde mora a ressalva de atribuição. */
  footer?: React.ReactNode;
  suffix?: string;
  /** `brand` = o ÚNICO número em destaque da tela (fundo violeta). */
  tone?: 'default' | 'brand';
  /** Micro-rótulo à direita do título ("30 dias"). */
  tag?: string;
}

/**
 * Um KPI, ligado ao vocabulário.
 *
 * O componente **não recebe rótulo nem frase de vazio**: recebe o `id` da métrica e vai buscar
 * os dois em `METRICA`. É o que garante que o texto de "sem dados" de uma métrica seja o
 * texto DAQUELA métrica — a alternativa é cada tela escrever o seu, e três telas depois
 * "Cobertura de atribuição" está explicando que nenhum monitorador foi conectado.
 *
 * Nenhuma conta aqui. `value` e `delta` chegam prontos do servidor (invariante 3).
 */
export function MetricTile({
  id,
  value,
  delta,
  comparedTo = 'vs. período anterior',
  size = 'default',
  loading = false,
  footer,
  suffix,
  tone = 'default',
  tag,
}: MetricTileProps) {
  const spec = METRICA[id];

  return (
    <StatTile
      label={spec.label}
      value={value}
      noDataReason={spec.semDados}
      loading={loading}
      size={size}
      /* Com micro-rótulo ("30 dias"), o canto é dele — como no UI kit; o ícone fica nos compactos. */
      {...(!tag && 'icon' in spec && spec.icon ? { icon: spec.icon } : {})}
      {...('format' in spec && spec.format ? { format: spec.format } : {})}
      {...('abbreviate' in spec && spec.abbreviate ? { abbreviate: spec.abbreviate } : {})}
      {...(suffix ? { suffix } : {})}
      /*
       * G2 (07/10): todo tile comparável mostra a variação — e, sem base, um "—" cujo tooltip
       * diz "sem período anterior para comparar". Exceção que continua valendo da v2: o VALOR
       * sem dado não ganha um segundo travessão ao lado (o painel vazio viraria oito "— —").
       */
      {...(delta !== undefined && (delta !== null || value !== null)
        ? { delta: { value: delta, polarity: spec.polarity, comparedTo } }
        : {})}
      tone={tone}
      {...(tag ? { tag } : {})}
      /*
       * "Como este número é medido" virou um (?) ao lado do valor: a definição continua a um
       * toque de distância, sem ocupar uma linha em cada um dos oito tiles.
       */
      annotation={
        'ajuda' in spec && spec.ajuda ? (
          <Tooltip content={spec.ajuda} side="bottom">
            <span
              tabIndex={0}
              aria-label="Como este número é medido"
              className={cn(
                'inline-flex cursor-help rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                tone === 'brand' ? 'text-primary-foreground/70' : 'text-subtle-foreground',
              )}
            >
              <CircleHelp aria-hidden="true" className="size-3.5" />
            </span>
          </Tooltip>
        ) : undefined
      }
      {...(footer ? { footer } : {})}
    />
  );
}
